import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { pt } from 'date-fns/locale';
import { useCountryRisk } from '../hooks/useCountryRisk';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { RISK_COLORS } from '../types';
import type { RiskLevel } from '../types';
import Spinner from '../components/ui/Spinner';
import Alert from '../components/ui/Alert';
import { bandKindOf, FLAGGED_SHARE_PCT } from '../lib/riskText';

const RISK_LABELS: Record<RiskLevel, string> = {
  Low: 'Baixo',
  Moderate: 'Moderado',
  High: 'Alto',
  Extreme: 'Extremo',
};

function Stat({ label, value, sub, accent }: { label: string; value: string; sub?: string; accent?: boolean }) {
  return (
    <div className="rounded-xl border border-ink-700 bg-ink-900 p-5">
      <div className="font-mono text-[11px] uppercase tracking-wider text-mist-faint">{label}</div>
      <div className={`mt-2 font-display text-3xl font-bold ${accent ? 'text-ember' : 'text-mist'}`}>{value}</div>
      {sub && <div className="mt-1 text-sm text-mist-muted">{sub}</div>}
    </div>
  );
}

function modPlus(summary: Record<string, number> | undefined): number {
  if (!summary) return 0;
  return (summary.Moderate || 0) + (summary.High || 0) + (summary.Extreme || 0);
}

export default function Dashboard() {
  useDocumentMeta(
    'Dashboard de risco de incêndio | Tutela IGNIS',
    'Resumo diário do risco de incêndio em Portugal: área em risco hoje, distribuição por nível, anomalias sazonais e tendência para amanhã.',
    '/ignis/dashboard',
  );
  const { data: today, isLoading, error } = useCountryRisk(undefined, 'today');
  const { data: tomorrow } = useCountryRisk(undefined, '24h');

  const total = today?.total_cells || 0;
  const summary = today?.risk_summary;
  const modPlusToday = modPlus(summary);
  const highExt = (summary?.High || 0) + (summary?.Extreme || 0);
  const pct = (n: number) => (total > 0 ? (n / total) * 100 : 0);

  // W5: wording keys on the served model family (share bands for the rebuilt model).
  const share = bandKindOf(today?.model_version) === 'share';

  // freshness: one issue a day on the daily path (06:15), so a map is fresh for
  // 30 h there; the legacy path refreshes every 3 h.
  const predTime = today?.prediction_time ? new Date(today.prediction_time) : null;
  const ageH = predTime ? (Date.now() - predTime.getTime()) / 3.6e6 : null;
  const fresh = ageH != null && ageH < (share ? 30 : 12);

  // today vs tomorrow (Moderate+ share)
  const todayPct = pct(modPlusToday);
  const tmrTotal = tomorrow?.total_cells || 0;
  const tmrPct = tmrTotal > 0 ? (modPlus(tomorrow?.risk_summary) / tmrTotal) * 100 : null;
  const delta = tmrPct != null ? tmrPct - todayPct : null;

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-ink-950 text-mist">
      <div className="mx-auto max-w-6xl px-6 py-12">
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.22em] text-ember">Módulo IGNIS</div>
            <h1 className="mt-2 font-display text-3xl font-bold tracking-tight text-mist">Dashboard</h1>
            <p className="mt-1 text-mist-muted">
              {format(new Date(), "EEEE, d 'de' MMMM 'de' yyyy", { locale: pt })}
            </p>
          </div>
          {today && (
            <div className="text-right">
              <div className="flex items-center justify-end gap-2 font-mono text-xs text-mist-muted">
                <span className={`h-2 w-2 rounded-full ${fresh ? 'bg-risk-low' : 'bg-risk-moderate'}`} />
                {predTime ? `${share ? 'Emitido' : 'Atualizado'} ${format(predTime, "d MMM, HH:mm", { locale: pt })}` : 'sem dados'}
              </div>
              <div className="mt-1 font-mono text-xs text-mist-faint">{today.model_version}</div>
            </div>
          )}
        </div>

        {isLoading && (
          <div className="mt-10 flex items-center gap-3 text-mist-muted">
            <Spinner />
            A carregar previsão nacional...
          </div>
        )}
        {error && <div className="mt-10"><Alert variant="error">Erro ao carregar a previsão nacional</Alert></div>}

        {today && !isLoading && (
          <>
            {/* Stat row */}
            <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              <Stat label="Cobertura" value={total.toLocaleString('pt-PT')} sub="células de 1 km" />
              <Stat
                label={share ? 'Área sinalizada hoje' : 'Em risco hoje'}
                value={`${pct(modPlusToday).toFixed(1)}%`}
                sub={
                  share
                    ? `${modPlusToday.toLocaleString('pt-PT')} células (Moderado+); quota fixa de ${FLAGGED_SHARE_PCT}% por construção`
                    : `${modPlusToday.toLocaleString('pt-PT')} células (Moderado+)`
                }
              />
              <Stat
                label="Risco alto ou extremo"
                value={highExt.toLocaleString('pt-PT')}
                sub={share ? 'células (3% do território, por ordem de risco)' : 'células'}
                accent
              />
              {share ? (
                <Stat
                  label="Emissão"
                  value={predTime ? format(predTime, 'HH:mm', { locale: pt }) : 'sem dados'}
                  sub={`um mapa por dia, hoje e amanhã${today.for_date ? ` · mapa para ${today.for_date}` : ''}`}
                />
              ) : (
                <Stat
                  label="Anomalias sazonais"
                  value={(today.anomaly_count ?? 0).toLocaleString('pt-PT')}
                  sub="top 5% para este mês"
                />
              )}
            </div>

            <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-3">
              {/* Distribution */}
              <div className="lg:col-span-2 rounded-xl border border-ink-700 bg-ink-900 p-6">
                <h2 className="font-display text-lg font-bold text-mist">Risco hoje</h2>
                {summary ? (
                  <div className="mt-5 space-y-2.5">
                    {(['Extreme', 'High', 'Moderate', 'Low'] as RiskLevel[]).map((level) => {
                      const count = summary[level] || 0;
                      const p = pct(count);
                      return (
                        <div key={level} className="flex items-center gap-3 text-sm">
                          <span className="h-3 w-3 flex-shrink-0 rounded-sm" style={{ backgroundColor: RISK_COLORS[level] }} />
                          <span className="w-20 text-mist">{RISK_LABELS[level]}</span>
                          <div className="h-2 flex-1 rounded-full bg-ink-800">
                            <div className="h-2 rounded-full" style={{ width: `${Math.max(p, 0.4)}%`, backgroundColor: RISK_COLORS[level] }} />
                          </div>
                          <span className="w-28 text-right font-mono text-mist-muted">
                            {count.toLocaleString('pt-PT')} ({p.toFixed(1)}%)
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="mt-4 text-mist-muted">Sem distribuição disponível.</p>
                )}

                {/* Today vs tomorrow */}
                {delta != null && (
                  <div className="mt-6 flex items-center gap-3 border-t border-ink-800 pt-4 text-sm">
                    <span className="font-mono text-[11px] uppercase tracking-wider text-mist-faint">Hoje vs amanhã</span>
                    <span className="text-mist-muted">Área em risco (Moderado+)</span>
                    <span
                      className={`ml-auto font-mono font-medium ${
                        delta > 0.2 ? 'text-ember' : delta < -0.2 ? 'text-risk-low' : 'text-mist-muted'
                      }`}
                    >
                      {todayPct.toFixed(1)}% {delta >= 0 ? '↑' : '↓'} {tmrPct!.toFixed(1)}%
                    </span>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="space-y-3">
                <h2 className="font-display text-lg font-bold text-mist">Ações</h2>
                <Link
                  to="/ignis/mapa-nacional"
                  className="flex items-center justify-between rounded-xl border border-ink-700 bg-ink-900 px-5 py-4 text-mist transition-colors hover:bg-ink-800"
                >
                  <span>Ver o mapa nacional</span>
                  <span className="text-mist-faint">→</span>
                </Link>
                <Link
                  to="/ignis/map"
                  className="flex items-center justify-between rounded-xl border border-ink-700 bg-ink-900 px-5 py-4 text-mist transition-colors hover:bg-ink-800"
                >
                  <span>Consultar um ponto</span>
                  <span className="text-mist-faint">→</span>
                </Link>
                <Link
                  to="/ignis/modelos"
                  className="flex items-center justify-between rounded-xl border border-ink-700 bg-ink-900 px-5 py-4 text-mist transition-colors hover:bg-ink-800"
                >
                  <span>Os modelos</span>
                  <span className="text-mist-faint">→</span>
                </Link>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
